// AtlasHoverCard — Story #6837 Tasks #6904 and #6910 (the card in screen-M4-atlas-sector-lens.png).
//
// Shown on the Sector lens when the pointer is on an invention's footprint, dots or star: its
// name, primary sector, "Also" secondary sectors, the sector value for the chosen country, the
// invention's assessment score and modelled SOM, and a link to its Market tab.
//
// The assessment and SOM are NOT on the own-scope data this lens reads (that would mean decrypting every
// owned project on every Atlas load), so they are fetched for the one invention under the pointer
// (decision d0011-6895, option B): GET /workflow/assessment/{id} and GET /market/{id}. Nothing is fetched
// until the pointer has rested on the invention for HOVER_INTENT_MS, so moving across the map costs
// nothing, and each answer is cached per invention so a second hover is instant. Both figures are AI
// output and the card says so.
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { X } from 'lucide-react'
import { sectorColor } from '../../lib/sectorColors'
import { getAssessment, getMarket } from '../../services/api'
import { currencyPrefix, dateLabel, rangeText } from '../market/marketFormat'
import type { AssessmentApiResponse } from '../../types'
import type { MarketProject } from '../../types/market'

/** How long the pointer must rest on an invention before its figures are fetched. */
export const HOVER_INTENT_MS = 250
/** A fetched answer stays fresh for this long, so a second hover reads the cache. */
const FRESH_MS = 5 * 60_000

export interface HoverCardSector { section: string; name: string | null }

export interface AtlasHoverCardProps {
  projectId: string
  name: string
  /** null: this invention has not been categorised yet. */
  primary: HoverCardSector | null
  also: HoverCardSector[]
  /** e.g. "US$95.6bn (2025)"; null when no official value is loaded for the primary sector. */
  value: string | null
  /** ISO3 of the chosen country: the market read for the SOM is for this country. */
  country: string
  countryLabel: string
  onClose: () => void
  /** The two per-hover reads; the real API unless a test supplies its own. */
  loadAssessment?: (projectId: string) => Promise<AssessmentApiResponse>
  loadMarket?: (projectId: string, country: string) => Promise<MarketProject>
}

const label = (s: HoverCardSector) => `${s.section}${s.name ? ` ${s.name}` : ''}`

/** "7.2 / 10", or why there is none. The score is the same overall score the aggregator heat map reads. */
export function assessmentText(a: AssessmentApiResponse | undefined): string {
  const x = a?.assessment
  if (!x) return 'Not assessed yet'
  if (x.recommendation === 'insufficient_basis') return 'Not enough to assess'
  return typeof x.overall_score === 'number' ? `${(Math.round(x.overall_score * 10) / 10).toFixed(1)} / 10` : 'Not assessed yet'
}

/** "A$150 – 300m · 17 Oct 2026", with a note when the range was built for a sector that is no longer primary. */
export function somText(m: MarketProject | undefined): string {
  const r = m?.range
  if (!r || !Array.isArray(r.som) || r.som.length !== 2) return 'Not modelled yet'
  const when = dateLabel(r.generated_at)
  return `${rangeText(r.som[0], r.som[1], currencyPrefix(r.currency))}${when ? ` · ${when}` : ''}${r.stale ? ' (out of date)' : ''}`
}

export default function AtlasHoverCard({ projectId, name, primary, also, value, country, countryLabel, onClose, loadAssessment = getAssessment, loadMarket = getMarket }: AtlasHoverCardProps) {
  // Hover intent: the fetch is armed only once the pointer has stayed on this invention.
  const [armed, setArmed] = useState(false)
  useEffect(() => {
    setArmed(false)
    const t = window.setTimeout(() => setArmed(true), HOVER_INTENT_MS)
    return () => window.clearTimeout(t)
  }, [projectId])
  const categorised = !!primary
  const assessment = useQuery({
    queryKey: ['atlas-hover-assessment', projectId],
    queryFn: () => loadAssessment(projectId),
    enabled: armed && categorised,
    staleTime: FRESH_MS,
    retry: false,
  })
  // The same key the Market tab reads, so opening the tab after a hover is instant too.
  const market = useQuery({
    queryKey: ['market', projectId, country],
    queryFn: () => loadMarket(projectId, country),
    enabled: armed && categorised,
    staleTime: FRESH_MS,
    retry: false,
  })
  const figure = (q: { isError: boolean; data: unknown }, text: () => string) =>
    q.isError ? "Couldn't load" : q.data === undefined ? 'Loading…' : text()
  return (
    <div
      data-testid="atlas-hover-card"
      role="dialog"
      aria-label={`${name}: sector summary`}
      className="absolute left-2 top-2 z-10 w-64 rounded-lg border border-[rgba(12,74,110,0.12)] bg-white/95 p-3 text-xs text-gray-600 shadow-md"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-bold text-iflow-900" data-testid="atlas-hover-name">{name}</p>
        <button type="button" onClick={onClose} aria-label="Close" className="rounded p-0.5 text-gray-400 hover:text-gray-700">
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
      {primary ? (
        <>
          <p className="mt-1" data-testid="atlas-hover-primary">
            <span className="mr-1.5 inline-block h-2.5 w-2.5 rounded-full align-middle" style={{ background: sectorColor(primary.section) }} aria-hidden />
            <span className="font-semibold text-gray-700">{label(primary)}</span>
          </p>
          {also.length > 0 && (
            <p className="mt-1" data-testid="atlas-hover-also"><span className="font-semibold text-gray-500">Also:</span> {also.map(label).join(' · ')}</p>
          )}
          <p className="mt-1" data-testid="atlas-hover-value">
            <span className="font-semibold text-gray-500">Sector value · {countryLabel}:</span> {value ?? '—'}
          </p>
          <p className="mt-1" data-testid="atlas-hover-assessment">
            <span className="font-semibold text-gray-500">Assessment (AI):</span> {figure(assessment, () => assessmentText(assessment.data))}
          </p>
          <p className="mt-1" data-testid="atlas-hover-som">
            <span className="font-semibold text-gray-500">Modelled SOM (AI) · {countryLabel}:</span> {figure(market, () => somText(market.data))}
          </p>
        </>
      ) : (
        <p className="mt-1" data-testid="atlas-hover-uncategorised">Not categorised yet.</p>
      )}
      <Link to={`/project/${projectId}?tab=market`} data-testid="atlas-hover-open" className="mt-2 inline-block font-semibold text-iflow-700 underline">
        Open Market tab →
      </Link>
    </div>
  )
}
